import React, { useState, useMemo } from "react";
import { Modal, ModalHeader } from "../../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../../globalComponents/ui/Button";
import {
  SettingsModalBody, SettingsModalFooter,
  SettingsFormGroup, SettingsFormLabel,
} from "../../Settings/SettingShell";
import MultiSelectDropdown from "../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import TagFilterBar from "../../Settings/Components/TagFilterBar";
import EvidenceFormFields from "./EvidenceFormFields";
import { useReportingQuestionsMap } from "../hooks/useReportingQuestionsMap";
import { getMatchingQuestionIds } from "../utils/kpiTagMatching";
import { uploadFileMetadata, uploadQuantitativeEvidence } from "../../../_services/documentsService";

const emptyState = {
  financialYearId: "",
  sourceId: "",
  subLocationId: "",
  kpiIds: [],
  userPrompt: "",
  grouped: true,
  files: [],
};

// Evidence upload is always EVIDENCE / QUANTITATIVE, question-less — handles 1..N files.
const BatchUploadModal = ({ show, onClose, onUploaded, financialYears, sources }) => {
  const [form, setForm] = useState(emptyState);
  const [filterSteps, setFilterSteps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { map: reportingQuestionsMap, options: kpiOptions } = useReportingQuestionsMap(form.financialYearId, "quantitative");

  // Same tag filter everywhere — narrows which KPIs are selectable here,
  // exactly like it narrows the evidence list.
  const matchingQuestionIds = useMemo(() => getMatchingQuestionIds(reportingQuestionsMap, filterSteps), [reportingQuestionsMap, filterSteps]);
  const visibleKpiOptions = useMemo(
    () => (matchingQuestionIds === null ? kpiOptions : kpiOptions.filter((o) => matchingQuestionIds.has(o.value))),
    [kpiOptions, matchingQuestionIds]
  );

  const handleClose = () => {
    if (uploading) return;
    setForm(emptyState);
    setFilterSteps([]);
    setError("");
    onClose();
  };

  const canSubmit = form.financialYearId && form.sourceId && form.kpiIds.length > 0 && form.files.length > 0;

  const handleFilesPicked = (fileList) => {
    setForm((f) => ({ ...f, files: [...f.files, ...Array.from(fileList)] }));
  };

  const removeFile = (idx) => {
    setForm((f) => ({ ...f, files: f.files.filter((_, i) => i !== idx) }));
  };

  const handleUpload = async () => {
    if (!canSubmit) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await Promise.all(form.files.map((file) => uploadFileMetadata(file)));
      const files = uploaded.map(({ fileMetadataId, fileMetadata, isExisting }) => ({
        fileMetadataId,
        documentType: "OTHERS",
        fileMetadata,
        isExisting,
      }));
      const batch = await uploadQuantitativeEvidence({
        financialYearId: Number(form.financialYearId),
        locationId: Number(form.sourceId),
        subLocationId: form.subLocationId ? Number(form.subLocationId) : undefined,
        kpiIds: form.kpiIds,
        userPrompt: form.userPrompt || undefined,
        grouped: form.grouped,
        files,
      });
      onUploaded?.(batch);
      handleClose();
    } catch (e) {
      setError(e?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth={640}>
      <ModalHeader icon="fas fa-upload" title="Upload Evidence" subtitle="Upload one or more files under one business context" onClose={handleClose} />
      <SettingsModalBody>
        <EvidenceFormFields
          financialYears={financialYears}
          financialYearId={form.financialYearId}
          onFinancialYearChange={(v) => setForm((f) => ({ ...f, financialYearId: v, kpiIds: [] }))}
          sources={sources}
          sourceId={form.sourceId}
          onSourceChange={(v) => setForm((f) => ({ ...f, sourceId: v }))}
          subLocationId={form.subLocationId}
          onSubLocationChange={(v) => setForm((f) => ({ ...f, subLocationId: v }))}
          showLocation
        />

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <SettingsFormLabel>Filter KPIs by tag</SettingsFormLabel>
          <TagFilterBar
            allQuestions={Object.values(reportingQuestionsMap)}
            filterSteps={filterSteps}
            onFilterStepsChange={setFilterSteps}
            pendingIds={new Set(form.kpiIds)}
          />
        </SettingsFormGroup>

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <SettingsFormLabel>KPIs to search for *</SettingsFormLabel>
          <MultiSelectDropdown
            options={visibleKpiOptions}
            selected={form.kpiIds}
            onChange={(kpiIds) => setForm((f) => ({ ...f, kpiIds }))}
            placeholder={form.financialYearId ? "Select KPIs…" : "Select a financial year first"}
          />
        </SettingsFormGroup>

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <SettingsFormLabel>Instructions for AI extraction (optional)</SettingsFormLabel>
          <textarea
            value={form.userPrompt}
            onChange={(e) => {
              const value = e.target.value;
              setForm((f) => ({ ...f, userPrompt: value }));
            }}
            placeholder="e.g. Only extract values for the Mumbai facility meter"
            style={{ width: "100%", minHeight: 60, padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
          />
        </SettingsFormGroup>

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.grouped}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm((f) => ({ ...f, grouped: checked }));
              }}
            />
            Group these files as one entry {form.files.length > 1 && "(uncheck to list each file separately)"}
          </label>
        </SettingsFormGroup>

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <SettingsFormLabel>Files * (up to 20)</SettingsFormLabel>
          <div
            onClick={() => document.getElementById("evidence-batch-input")?.click()}
            style={{ border: "2px dashed #3f88a5", borderRadius: 10, padding: "22px 16px", textAlign: "center", cursor: "pointer", background: "#f8fbfc" }}
          >
            <i className="fas fa-cloud-upload-alt" style={{ fontSize: 20, color: "#3f88a5" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3f88a5", marginTop: 6 }}>Click to browse files</div>
            <input
              id="evidence-batch-input"
              type="file"
              multiple
              hidden
              onChange={(e) => e.target.files.length && handleFilesPicked(e.target.files)}
            />
          </div>

          {form.files.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              {form.files.map((file, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 12px" }}>
                  <i className="fas fa-file-alt" style={{ color: "#3f88a5" }} />
                  <span style={{ flex: 1, fontSize: 12.5, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {file.name}
                  </span>
                  <button onClick={() => removeFile(idx)} style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8" }}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </SettingsFormGroup>

        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fee2e2", border: "1px solid #fca5a5", fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
            {error}
          </div>
        )}
      </SettingsModalBody>
      <SettingsModalFooter>
        <SecondaryButton onClick={handleClose} disabled={uploading}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleUpload} disabled={!canSubmit || uploading}>
          {uploading ? "Uploading…" : `Upload ${form.files.length || ""} File${form.files.length === 1 ? "" : "s"}`}
        </PrimaryButton>
      </SettingsModalFooter>
    </Modal>
  );
};

export default BatchUploadModal;
